import {useContext, useEffect, useState} from 'react';
import {observer} from 'mobx-react';
import {NetworkContext} from '../lib/context';
import {UPLOAD_JOB_PHASE} from '../lib/types';
import ProviderLocationsTable from '../components/provider-location-table';
// import MemberLocationsTable from '../components/member-location-table';
import ProcessingFiles from '../components/processing-files';
import {Button, Tabs, TabList, Tab, TabPanels, TabPanel} from '@carbon/react';
import {Upload} from '@carbon/icons-react';
import LoadingModal from '../components/loading-modal';
import NetworkOptimizationErrorBoundary from '../components/error-boundary';
import ToastNotifications from '../components/toast-notifications';
import UploadFilesModal from '../components/upload-files-modal';
import {ERROR_DOMAINS} from '../../../lib/error-handler';
import {safeConsoleError} from '../../../lib/sanitize';
import './myFilesNew.scss';

const MyFilesNew = observer(() => {
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);

    const networkOptimizationStore = useContext(NetworkContext);

    // Use store's loading state and data directly
    const loading = isInitialLoading || networkOptimizationStore.loadingProviderRosters;
    const rows = networkOptimizationStore.providerRosters;
    const rosterError = networkOptimizationStore.getDomainError(ERROR_DOMAINS.ROSTERS);
    const processingError = networkOptimizationStore.getDomainError(ERROR_DOMAINS.PROCESSING_FILES);

    const handleCopyRoster = async (rosterId: number, revisionName: string, notes: string) => {
        const abortController = new AbortController();
        try {
            await networkOptimizationStore.copyRoster(rosterId, revisionName, notes, abortController);
        } catch (error) {
            safeConsoleError('Failed to copy roster:', error);
            throw error;
        }
    };

    const handleRenameRoster = async (rosterId: number, revisionId: number, revisionName: string, notes: string | null) => {
        const abortController = new AbortController();
        try {
            await networkOptimizationStore.renameRoster(rosterId, revisionId, revisionName, notes, abortController);
        } catch (error) {
            safeConsoleError('Failed to rename roster:', error);
            throw error;
        }
    };

    const handleDeleteRoster = async (rosterId: number, revisionId: number) => {
        const abortController = new AbortController();
        try {
            await networkOptimizationStore.deleteRoster(rosterId, revisionId, abortController);
        } catch (error) {
            safeConsoleError('Failed to delete roster:', error);
            throw error;
        }
    };

    const completedUploadCount = networkOptimizationStore.uploadJobs.filter(j => j.phase === UPLOAD_JOB_PHASE.DONE).length;

    useEffect(() => {
        if (completedUploadCount === 0) return;
        const abortController = new AbortController();
        networkOptimizationStore.loadProcessingFiles(abortController);
        return () => abortController.abort();
    }, [completedUploadCount, networkOptimizationStore]);

    useEffect(() => {
        const abortController = new AbortController();
        setIsInitialLoading(true);
        networkOptimizationStore.reset();

        const loadData = async () => {
            try {
                if (networkOptimizationStore.providerRosters.length === 0 && !networkOptimizationStore.loadingProviderRosters) {
                    await networkOptimizationStore.loadProviderRosters(abortController);
                }

                await networkOptimizationStore.loadProcessingFiles(abortController);

                // TODO: Implement patient rosters when backend support is available
                // if (networkOptimizationStore.patientRosters.length === 0 && !networkOptimizationStore.loadingPatientRosters) {
                //     await networkOptimizationStore.loadPatientRosters(abortController);
                // }

                networkOptimizationStore.checkForExistingStreams();
            } catch (error) {
                if (networkOptimizationStore.isAbortError(error)) {
                    // Request was aborted, do not show error
                    return;
                } 
            } finally {
                setIsInitialLoading(false);
            }
        };

        loadData();
        return () => abortController.abort();
    }, [networkOptimizationStore]);

    return (
        <NetworkOptimizationErrorBoundary>
            <div className="min-h-[calc(100vh-11rem+2px)] w-full overflow-hidden myFilesNew">
                <div className="page-header">
                    <div className="header-content">
                        <h1 className="page-title">My Files</h1>
                        <p className="page-subtitle">Choose to upload a new file or browse saved files</p>
                    </div>
                    <Button
                        kind="primary"
                        renderIcon={Upload}
                        onClick={() => setUploadModalOpen(true)}
                        disabled={loading}
                    >
                        Upload Files
                    </Button>
                </div>
                <div className="my-files-tabs-container">
                    <Tabs selectedIndex={selectedTabIndex} onChange={(evt) => setSelectedTabIndex(evt.selectedIndex)}>
                        <TabList aria-label="My Files tabs" className='tab-list-container'>
                            <Tab>Available Files ({rows.length})</Tab>
                            <Tab>
                                Processing <span className="processing-badge">{networkOptimizationStore.processingFilesTotalCount}</span>
                            </Tab>
                        </TabList>
                        <TabPanels>
                            <TabPanel>
                                <div className="tab-content-wrapper">
                                    <ProviderLocationsTable
                                        rows={rows}
                                        loading={loading}
                                        error={rosterError}
                                        onCopyRoster={handleCopyRoster}
                                        onRenameRoster={handleRenameRoster}
                                        onDeleteRoster={handleDeleteRoster}
                                    />
                                </div>
                            </TabPanel>
                            <TabPanel>
                                <ProcessingFiles onSwitchToAvailableFiles={() => setSelectedTabIndex(0)} error={processingError} />
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </div>

                {/* Loading Modal */}
                <LoadingModal open={networkOptimizationStore.isRosterActionLoading} />

                <UploadFilesModal
                    open={uploadModalOpen}
                    onClose={() => setUploadModalOpen(false)}
                    onUpload={(files) => networkOptimizationStore.startFileUploads(files)}
                    existingFileNames={[
                        ...networkOptimizationStore.providerRosters.map((r: any) => r.rosterName),
                        ...networkOptimizationStore.processingFiles.map(f => f.fileName),
                    ]}
                    uploadingFileNames={networkOptimizationStore.uploadJobs
                        .filter(j => j.phase !== UPLOAD_JOB_PHASE.ERROR && j.phase !== UPLOAD_JOB_PHASE.INTERRUPTED)
                        .map(j => j.filename)}
                />

                {/* Toast Notifications Queue */}
                <ToastNotifications />
            </div>
        </NetworkOptimizationErrorBoundary>
    );
});

export default MyFilesNew;
